import { useCallback } from 'react';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { RestoreGroupSubscriptionActionData } from '~/routes/__actions/restore_group_subscription';

/**
 * Restore ("Keep subscription") returns `{ message }` on success / `{ errors }`
 * on 422, so the caller only needs an `ok` + flattened `error`. Non-destructive
 * positive action: the host fires it directly (no confirm) and toasts the result.
 */
export type RestoreSubscriptionResult = {
  ok: boolean;
  error: string | null;
};

export type UseRestoreSubscriptionResult = {
  restoreSubscription: (groupId: string) => Promise<RestoreSubscriptionResult>;
};

export function useRestoreSubscription(): UseRestoreSubscriptionResult {
  const restoreFetcher = useNonRevalidatingFetcher<() => Promise<RestoreGroupSubscriptionActionData>>();

  const restoreSubscription = useCallback(
    async (groupId: string): Promise<RestoreSubscriptionResult> => {
      const submitResult = await restoreFetcher.submit(JSON.stringify({ group_subscription_id: groupId }), {
        method: 'post',
        action: '/restore_group_subscription',
        encType: 'application/json',
      });
      const error = submitResult.data?.error ?? (submitResult.ok ? null : 'Failed to restore subscription');
      return { ok: submitResult.ok && !error, error };
    },
    [restoreFetcher],
  );

  return { restoreSubscription };
}
